import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function CursoGratuitoDetalhe() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [progress, setProgress] = useState({ watched_seconds: 0, completed: false });
  const tickRef = useRef(null);
  const startedAt = useRef(null);

  useEffect(() => {
    api.get(`/free-courses/${id}`).then(r => setC(r.data)).catch(() => {});
    api.get(`/free-courses/${id}/progress`).then(r => setProgress(r.data)).catch(() => {});
  }, [id]);

  // Track watch time: increment every 10 seconds while page is open
  useEffect(() => {
    if (!c || progress.completed) return;
    startedAt.current = Date.now();
    tickRef.current = setInterval(() => {
      setProgress(prev => {
        const next = { ...prev, watched_seconds: (prev.watched_seconds || 0) + 10 };
        // Auto-complete if reached >= duration
        const totalSec = (c.duration_minutes || 0) * 60;
        if (totalSec > 0 && next.watched_seconds >= totalSec) {
          next.completed = true;
        }
        // Persist async (best-effort)
        api.post(`/free-courses/${id}/progress`, { watched_seconds: next.watched_seconds, completed: next.completed }).catch(() => {});
        return next;
      });
    }, 10000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c, progress.completed]);

  const markCompleted = async () => {
    try {
      const { data } = await api.post(`/free-courses/${id}/progress`, { watched_seconds: progress.watched_seconds || 0, completed: true });
      setProgress(data);
      toast.success("Aula marcada como assistida!");
    } catch { toast.error("Erro ao marcar"); }
  };

  const reset = async () => {
    try {
      await api.post(`/free-courses/${id}/progress`, { watched_seconds: 0, completed: false });
      setProgress({ watched_seconds: 0, completed: false });
      toast.success("Progresso reiniciado");
    } catch {}
  };

  if (!c) return <Skeleton className="h-60 w-full rounded-xl" />;

  const totalSec = (c.duration_minutes || 0) * 60;
  const pct = totalSec > 0 ? Math.min(100, Math.round(((progress.watched_seconds || 0) / totalSec) * 100)) : 0;

  return (
    <div className="space-y-4" data-testid="free-course-detail">
      <Card className="rounded-2xl overflow-hidden">
        <AspectRatio ratio={16/9}>
          <iframe
            data-testid="free-course-player"
            src={c.video_url}
            title={c.title}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </AspectRatio>
        <CardContent className="p-5">
          <div className="flex flex-wrap gap-2 mb-2">
            <Badge variant="secondary" className="capitalize">{c.area}</Badge>
            <Badge variant="outline">{c.duration_minutes} min</Badge>
            {c.category && <Badge variant="outline">{c.category}</Badge>}
            {progress.completed && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Assistida</Badge>}
          </div>
          <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">{c.title}</h1>
          <p className="text-sm text-muted-foreground mt-2">{c.description}</p>
          {c.professor && <p className="text-xs text-muted-foreground mt-2">Professor(a): {c.professor}</p>}

          <div className="mt-4">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Progresso</span>
              <span className="font-mono-id">{Math.floor((progress.watched_seconds || 0) / 60)} min assistidos {totalSec ? `de ${c.duration_minutes} min` : ""}</span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, background: "linear-gradient(90deg, #1e6fd9 0%, #c9a961 100%)" }}
              />
            </div>
            <div className="mt-3 flex gap-2">
              {!progress.completed && (
                <Button size="sm" onClick={markCompleted} data-testid="course-mark-completed"><CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como assistida</Button>
              )}
              {progress.completed && (
                <Button size="sm" variant="outline" onClick={reset} data-testid="course-reset"><RotateCcw className="w-4 h-4 mr-1" /> Reiniciar</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
